import { ChangeDetectorRef, Component, Inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { AbstractListDialogComponent, ListDialogData } from '@gipi-pages/abstract/abstract-list-dialog.component';
import { SortModel } from '@gipi-pages/abstract/models/sort.model';
import { ClientClassificationModel } from '@gipi-pages/client-classification/models/client-classification.model';
import { ClientClassificationFilterDTO } from '@gipi-pages/client-classification/models/dto/client-classification-filter.dto';
import { ClientClassificationService } from '@gipi-pages/client-classification/services/client-classification.service';
import { CheckboxComponent } from '@gipi-ui/components/checkbox/checkbox.component';
import { TablePaginatorEvent } from '@gipi-ui/components/mat-table/mat-table.component';
import { TableColumn } from '@gipi-ui/components/table/shared/table-column';
import { TableColumnBuilder } from '@gipi-ui/components/table/shared/table-column-builder';
import { AlertService } from '@gipi-ui/services/alert.service';
import { ArrayUtil } from '@gipi-ui/utils/array.util';
import { ObjectUtil } from '@gipi-ui/utils/object.util';
import { StringUtil } from '@gipi-ui/utils/string.util';

@Component({
    selector: 'gipi-client-classification-list-dialog',
    templateUrl: './client-classification-list-dialog.component.html',
    styleUrls: ['./client-classification-list-dialog.component.scss']
})
export class ClientClassificationListDialogComponent extends AbstractListDialogComponent<ClientClassificationModel, ClientClassificationFilterDTO> implements OnInit {

    @ViewChild('checkboxAllTemplate', { static: true }) checkboxAllTemplate: TemplateRef<CheckboxComponent>;
    @ViewChild('checkboxRowTemplate', { static: true }) checkboxRowTemplate: TemplateRef<CheckboxComponent>;

    public optionsClientClassification: ClientClassificationModel[] = [];
    public clientClassification: ClientClassificationModel = null;

    public allRowsChecked: boolean = false;
    public indeterminateRowsChecked: boolean = false;

    public entitiesSelected: ClientClassificationModel[] = [];

    public get entitiesSelectedIsEmpty(): boolean {
        return ArrayUtil.isEmpty(this.entitiesSelected);
    }

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _alertService: AlertService,
        protected service: ClientClassificationService,
        protected dialogRef: MatDialogRef<ClientClassificationListDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ListDialogData<ClientClassificationModel> = { entitiesSelected: [], selectSingle: false },
    ) {
        super(service, dialogRef, data);
    }

    ngOnInit(): void {
        super.ngOnInit();

        if (!ObjectUtil.isNull(this.data) && !ArrayUtil.isEmpty(this.data.entitiesSelected)) {
            this.entitiesSelected = ArrayUtil.clone(this.data.entitiesSelected);
        }
    }

    protected _newFilter(): ClientClassificationFilterDTO {
        const filter: ClientClassificationFilterDTO = new ClientClassificationFilterDTO();
        filter.researchField = '';
        filter.sorts = [];
        return filter;
    }

    protected _createTableColumns(): TableColumn[] {
        return [
            TableColumnBuilder
                .instance()
                .property('checkbox')
                .templateHeader(this.checkboxAllTemplate)
                .template(this.checkboxRowTemplate)
                .action(() => { })
                .width(60)
                .align('center')
                .build(),
            TableColumnBuilder
                .instance()
                .property('description')
                .description('Descrição')
                .value((obj: ClientClassificationModel) => !StringUtil.isEmpty(obj.description) ? obj.description : '')
                .sortable(true)
                .sliceLength(50)
                .build(),
            TableColumnBuilder
                .instance()
                .property('financialMessage')
                .description('Mensagem financeira')
                .sliceLength(50)
                .value((obj: ClientClassificationModel) => !StringUtil.isEmpty(obj.financialMessage) ? obj.financialMessage : '')
                .build(),
            TableColumnBuilder
                .instance()
                .property('financialObservation')
                .description('Observação financeira')
                .sliceLength(50)
                .value((obj: ClientClassificationModel) => !StringUtil.isEmpty(obj.financialObservation) ? obj.financialObservation : '')
                .build(),
        ];
    }

    public confirm(): void {
        this.dialogRef.close(this.entitiesSelected);
    }

    public close(): void {
        this.dialogRef.close(this.data.entitiesSelected);
    }

    public findAll(pageEvent?: TablePaginatorEvent): void {
        try {
            this.loading = true;

            this.page = this._newPage();
            if (ObjectUtil.isNull(this.filter)) {
                this.filter = this._newFilter();
            }

            this.filter.sorts = [];
            if (pageEvent) {
                this.filter.pageNumber = pageEvent.pageIndex;
                this.filter.pageSize = pageEvent.pageSize;

                if (!ObjectUtil.isNull(pageEvent.sort) && !StringUtil.isEmpty(pageEvent.sort.field)) {
                    this.filter.sorts = [new SortModel(pageEvent.sort.field, (pageEvent.sort.direction || 'DESC'))];
                }
            } else {
                this.filter.pageNumber = 0;
                this.filter.pageSize = 10;
            }

            this.service.findByValue(this.filter.researchField, this.filter.pageNumber, this.filter.pageSize, this.filter.sorts[0]).toPromise().then(page => {
                this.page = page;
                this._resetRowsSelected();
                this._remarkRowsSelected();

                this.loading = false;
            }, error => {
                this.loading = false;
                throw new Error(error);
            });
        } catch (e) {
            this.loading = false;
            throw new Error(e);
        } finally {
            this._changeDetectorRef.detectChanges();
        }
    }

    private _resetRowsSelected(): void {
        this.allRowsChecked = false;
        this.indeterminateRowsChecked = false;

        for (let i = 0; i < this.page.content.length; i++) {
            this.page.content[i].selected = false;
        }

        this.validateAllRowsSelected();
    }

    private _remarkRowsSelected(): void {
        setTimeout(() => {
            if (!this.entitiesSelectedIsEmpty && !ArrayUtil.isEmpty(this.page.content)) {
                for (let i = 0; i < this.page.content.length; i++) {
                    const isSelected = this.entitiesSelected.findIndex(e => e.id === this.page.content[i].id) >= 0;
                    if (isSelected) {
                        this.page.content[i].selected = true;
                    }
                }

                this.validateAllRowsSelected();
            }
        });
    }

    public validateAllRowsSelected(): void {
        this.allRowsChecked = this.entitiesSelected.length > 0;
        this.indeterminateRowsChecked = this.entitiesSelected.length > 0 && this.entitiesSelected.length !== this.page.totalElements;
    }

    public checkAllRows(): void {
        this.entitiesSelected = [];
        for (let i = 0; i < this.page.content.length; i++) {
            this.page.content[i].selected = this.allRowsChecked;
            this.selectRow(this.page.content[i], false);
        }

        this.validateAllRowsSelected();
    }

    public selectRow(entity: ClientClassificationModel, validateAllSelected: boolean): void {
        if (entity.selected) {
            this.entitiesSelected.push(entity);
        } else {
            const index: number = this.entitiesSelected.findIndex((i) => i.id === entity.id);
            this.entitiesSelected.splice(index, 1);
        }

        if (validateAllSelected) {
            this.validateAllRowsSelected();
        }
    }

    public rowChange(entity: ClientClassificationModel): void {
        entity.selected = !entity.selected;
        this.selectRow(entity, true);
    }

}
